import { convert } from "../convert.js";
import { classifyMessages } from "../warnings.js";
import { buildPage, wrapFragment, htmlFilename } from "../export.js";
import { formatHtml } from "../format.js";
import { state, conversionProfile, setProfileId, openForEditing } from "../state.js";
import { el, actionButton, announce, setBusy, downloadBlob } from "../ui/dom.js";
import { createTabs } from "../ui/tabs.js";
import { previewFrame, showPreview, codeView } from "../ui/preview.js";
import { profileSelect } from "../ui/profile-select.js";
import { CONVERT_ERROR } from "./convert.js";

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

export function mount(root, route, nav) {
  if (!state.doc) {
    queueMicrotask(() => nav.go("#/"));
    return {};
  }
  const doc = state.doc;
  const title = doc.name.replace(/\.docx$/i, "");
  let shown = null; // { html, profile } currently on screen
  let token = 0;

  const framePanel = el("div", {}, previewFrame(`Preview of ${doc.name}`));
  const frame = framePanel.firstChild;
  const codePanel = el("div");
  const tabs = createTabs({ label: "Result view", tabs: [{ key: "preview", label: "Preview", panel: framePanel }, { key: "code", label: "HTML code", panel: codePanel }] });
  const notes = el("div", { class: "result-notes" });
  const panel = el("section", { class: "result-panel glass", "aria-label": "Result" }, tabs.list, framePanel, codePanel);
  const { wrapper: profileField } = profileSelect({
    id: "result-profile", label: "Styled with", value: conversionProfile().id,
    onChange: (id) => {
      setProfileId(id);
      run();
    },
  });

  const download = actionButton("Download", "HTML file", { class: "primary-btn", onclick: downloadHtml });
  const copy = actionButton("Copy", "HTML to clipboard", { onclick: copyHtml });
  const edit = actionButton("Edit this profile using my document", "", { onclick: () => openEditor("all") });
  const another = el("a", { href: "#/", class: "btn-like", onclick: () => { state.doc = null; state.result = null; } }, "Convert another document");

  function openEditor(show) {
    const profile = shown?.profile ?? conversionProfile();
    openForEditing(profile);
    state.previewSource = "document";
    state.editingFromResult = true;
    state.filters = { search: "", show, type: "any" };
    nav.go(`#/profiles/${encodeURIComponent(profile.id)}`);
  }

  function downloadHtml() {
    if (!shown) return;
    try {
      const page = buildPage({ html: shown.html, profile: shown.profile, title, mode: "export" });
      downloadBlob(formatHtml(page), htmlFilename(doc.name), "text/html");
      announce("HTML file downloaded.");
    } catch (error) {
      console.error(error);
      notes.prepend(el("p", { class: "fire-message", role: "alert" }, "Couldn't create the HTML file. Try again."));
    }
  }

  async function copyHtml() {
    if (!shown) return;
    try {
      await navigator.clipboard.writeText(formatHtml(wrapFragment(shown.html, shown.profile.wrapper)));
      copy.firstChild.textContent = "Copied";
      announce("HTML copied to the clipboard.");
      setTimeout(() => (copy.firstChild.textContent = "Copy"), 1500);
    } catch {
      notes.prepend(el("p", { class: "fire-message", role: "alert" }, "Couldn't copy. Open the HTML code tab and copy from there."));
    }
  }

  function renderNotes({ customStyles, builtinStyles, other }) {
    const blocks = [];
    if (customStyles.length) {
      blocks.push(el("div", { class: "fire-message" },
        el("p", {}, `${plural(customStyles.length, "style")} in your document ${customStyles.length === 1 ? "isn't" : "aren't"} mapped yet: `,
          el("strong", {}, customStyles.map((s) => s.name).join(", ")), "."),
        actionButton("Map them", "in the profile editor", { onclick: () => openEditor("unmapped") })));
    }
    if (builtinStyles.length) {
      blocks.push(el("details", {}, el("summary", {}, `Safe to ignore (${builtinStyles.length})`),
        el("p", { class: "hint" }, "These built-in Word styles are output as plain paragraphs or text."),
        el("ul", {}, builtinStyles.map((s) => el("li", {}, `${s.name} (${s.type} style)`)))));
    }
    if (other.length) {
      blocks.push(el("details", {}, el("summary", {}, `Conversion notes (${other.length})`),
        el("ul", {}, other.map((m) => el("li", {}, m.count > 1 ? `${m.message} (×${m.count})` : m.message)))));
    }
    notes.replaceChildren(...blocks);
  }

  async function run() {
    const mine = ++token;
    const profile = conversionProfile();
    const key = JSON.stringify(profile);
    if (state.result?.profileJson !== key) {
      setBusy(panel, true);
      try {
        const converted = await convert(doc.data, profile);
        if (mine !== token) return;
        state.result = { ...converted, profileJson: key };
      } catch (error) {
        console.error(error);
        if (mine === token) notes.replaceChildren(el("p", { class: "fire-message", role: "alert" }, CONVERT_ERROR));
        return;
      } finally {
        if (mine === token) setBusy(panel, false);
      }
      announce(`Converted with ${profile.name}.`);
    }
    shown = { html: state.result.html, profile };
    showPreview(frame, { html: shown.html, profile, title });
    codePanel.replaceChildren(codeView(shown.html, profile.wrapper));
    renderNotes(classifyMessages(state.result.messages));
  }

  root.append(
    el("div", { class: "result-head" },
      el("div", {}, el("h1", {}, doc.name), el("p", { class: "hint" }, "Converted in your browser.")),
      profileField),
    el("div", { class: "row", style: "margin-bottom:14px" }, download, copy, edit, another),
    notes,
    panel);
  run();
  return { title: `Result: ${doc.name}` };
}
